import { useCallback, useRef } from 'react';
import {
  Badge,
  Button,
  HStack,
  Icon,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Td,
  Text,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import DocumentList from 'components/documents/DocumentList';
import { FaCheckCircle } from 'react-icons/fa';
import { MdCancel } from 'react-icons/md';
import { useTranslation } from 'i18n/TranslationProvider';

const getStatusBadge = (status, t) => {
  switch (status) {
    case 'approved':
      return <Badge colorScheme="green">{t('Approved')}</Badge>;
    case 'pending':
      return <Badge colorScheme="orange">{t('Pending')}</Badge>;
    case 'rejected':
      return <Badge colorScheme="red">{t('Rejected')}</Badge>;
    case 'disabled':
      return <Badge colorScheme="gray">{t('Disabled')}</Badge>;
    default:
      return <Badge>{t('Unknown')}</Badge>;
  }
};

const PendingPartnerItem = ({
  partner,
  tableMode = false,
  onApprove,
  onReject,
  approveLoading = false,
  rejectLoading = false,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const cancelApproveRef = useRef();
  const cancelRejectRef = useRef();

  const {
    isOpen: isApproveOpen,
    onOpen: onApproveOpen,
    onClose: onApproveClose,
  } = useDisclosure();

  const {
    isOpen: isRejectOpen,
    onOpen: onRejectOpen,
    onClose: onRejectClose,
  } = useDisclosure();

  const docsDisclosure = useDisclosure();

  const showToast = useCallback(
    (title, status, description) => {
      toast({
        title,
        status,
        description,
        duration: 3000,
        isClosable: true,
      });
    },
    [toast],
  );

  const confirmApprove = async () => {
    const success = await onApprove(partner.firebaseUid);
    onApproveClose();
    if (success) {
      showToast(t('Partner approved successfully!'), 'success');
    } else {
      showToast(
        t('Failed to approve partner'),
        'error',
        t('There was an issue approving this partner.'),
      );
    }
  };

  const confirmReject = async () => {
    const success = await onReject(partner.firebaseUid);
    onRejectClose();
    if (success) {
      showToast(t('Partner rejected successfully.'), 'info');
    } else {
      showToast(
        t('Failed to reject partner'),
        'error',
        t('There was an issue rejecting this partner.'),
      );
    }
  };

  const actions = (
    <HStack spacing={2} flexWrap="wrap">
      <Button
        colorScheme="green"
        size="sm"
        w="36px"
        h="36px"
        borderRadius="full"
        onClick={onApproveOpen}
        isDisabled={approveLoading || partner.validationStatus === 'approved'}
      >
        <Icon as={FaCheckCircle} boxSize={4} />
      </Button>

      <Button
        colorScheme="red"
        size="sm"
        w="36px"
        h="36px"
        borderRadius="full"
        onClick={onRejectOpen}
        isDisabled={rejectLoading || partner.validationStatus === 'rejected'}
      >
        <Icon as={MdCancel} boxSize={4} />
      </Button>
    </HStack>
  );

  return (
    <>
      {tableMode ? (
        <Tr>
          <Td>
            <Text fontSize="sm" fontWeight="700" isTruncated>
              {partner.displayName || t('No name')}
            </Text>
          </Td>
          <Td>
            <Text fontSize="sm">{partner.email || '-'}</Text>
          </Td>
          <Td>
            <Text fontSize="sm">{partner.companyName || '-'}</Text>
          </Td>
          <Td>{getStatusBadge(partner.validationStatus, t)}</Td>
          <Td>
            <Text fontSize="sm" fontWeight="700">
              {partner?.quarterScore?.score ?? 100}%
            </Text>
          </Td>
          <Td>
            <Text fontSize="sm">
              +{partner?.quarterScore?.paymentDelayDays ?? 0} {t('days')}
            </Text>
          </Td>
          <Td>
            <Button size="xs" variant="outline" onClick={docsDisclosure.onOpen}>
              {t('View Documents')}
            </Button>
          </Td>
          <Td>{actions}</Td>
        </Tr>
      ) : null}

      <Modal isOpen={docsDisclosure.isOpen} onClose={docsDisclosure.onClose} size="4xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('Partner documents')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <DocumentList
              entityType="user"
              entityId={partner.firebaseUid}
              docType="verification"
            />
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* APPROVE DIALOG */}
      <AlertDialog
        isOpen={isApproveOpen}
        leastDestructiveRef={cancelApproveRef}
        onClose={onApproveClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t('Approve partner?')}
            </AlertDialogHeader>

            <AlertDialogBody>
              {t('This will approve the partner and grant access.')}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelApproveRef} onClick={onApproveClose}>
                {t('Cancel')}
              </Button>
              <Button
                colorScheme="green"
                ml={3}
                isLoading={approveLoading}
                onClick={confirmApprove}
              >
                {t('Approve')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* REJECT DIALOG */}
      <AlertDialog
        isOpen={isRejectOpen}
        leastDestructiveRef={cancelRejectRef}
        onClose={onRejectClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t('Reject partner?')}
            </AlertDialogHeader>

            <AlertDialogBody>
              {t('This action cannot be undone')}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRejectRef} onClick={onRejectClose}>
                {t('Cancel')}
              </Button>
              <Button
                colorScheme="red"
                ml={3}
                isLoading={rejectLoading}
                onClick={confirmReject}
              >
                {t('Reject')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default PendingPartnerItem;
